import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    A,
    ActivityIndicator,
    Box,
    DatePickerInput,
    Grid,
    Input,
    Icon,
    Select,
    Table,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import useTheme from '@saddlebackchurch/react-cm-ui/core/styles/useTheme';
import useMediaQuery from '@saddlebackchurch/react-cm-ui/core/utils/useMediaQuery';
import ClassNames from 'classnames';
import {
    isArray,
    isEmpty,
    isNaN,
    isNil,
} from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import {
    CurrencySymbol,
    SelectOption,
} from '../../../global/models';
import { isEnterKeyCode } from '../../../global/utils/utils.js';
import { getDesignationsFromCore } from '../api/designation.service';
import {
    getGivingPaymentHistory,
    requestExportStatus,
    requestGivingExcelDataExport,
} from '../api/givingHistory.service';
import Button from '../shared/components/button';
import ButtonGroup from '../shared/components/buttonGroup';
import NoResultFound from '../shared/components/noResultFound';
import { formatNumberAsCurrency } from '../shared/js/utils';
import {
    BEM_BLOCK_NAME,
    CURRENT_YEAR,
} from './constants';
import Error from './error';
import {
    GivingPaymentHistory,
    SearchType,
} from './models';

type PropTypes = {
    /**
     * This prop should contain the user's access token,
     * to allow the Payment Activity component to make
     * authorized API calls.
     *
     * **_REQUIRED:_**  The user **_MUST_** be authenticated to view his or her Giving History.
     */
    accessToken: string;

    /**
     * Boolean flag controlling whether or not this component is enabled.
     */
    enabled?: boolean;

    /**
     * Boolean flag indicating whether or not to include a spouse's data in Giving History query results
     * and on generated Giving Statements.
     */
    includeSpouseGiving: boolean;

    /**
     * Controls how many years of Giving History should be included.
     *
     * If set to 1, only includes the current year to date.
     *
     * If set to 2, includes the current year and previous year.
     *
     * If set to 3, includes the current year and previous two years.
     *
     * etc.
     */
    numberOfYearsToInclude?: number;

    /**
     * Boolean flag controlling whether or not to show the Excel Spreadsheet button for exporting the Giving History
     * data to an Excel spreadsheet (XLSX file).
     *
     * Optional; defaults to **`false`** (don't show the Excel Spreadsheet button).
     */
    showExcelDownloadButton?: boolean;

    /**
     * Boolean flag controlling whether or not to show the Search and Filtering controls for Giving History.
     *
     * Optional; defaults to **`false`** (don't show the Search and Filtering controls).
     */
    showSearchAndFilterControls?: boolean;

    /**
     * Boolean flag controlling whether or not to show the summary statistics on the Giving History data
     * (e.g. This Month, Year to Date, Previous Year to Date, Previous Year Total).
     *
     * Optional; defaults to **`false`** (don't show the statistics).
     */
    showStatistics?: boolean;

    /**
     * Boolean flag controlling whether or not to use Rock RMS compatible (v4) API endpoints.
     */
    useRockAPIs?: boolean;
};

const useStyles = makeStyles(({
    breakpoints,
    palette,
    spacing,
    typography,
}) => ({
    clearSearchLink: {
        alignitems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        marginTop: spacing(1),
        [breakpoints.down('sm')]: {
            flexDirection: 'row',
            marginTop: 0,
        },
        '& span': {
            fontWeight: 400,
        },
    },
    hidden: {
        display: 'none',
    },
    inputLabel: {
        '& .label': {
            color: palette.text.primary,
            fontWeight: typography.fontWeightMedium,
        },
    },
    root: {
        padding: `${spacing(3)}px ${spacing(2)}px`,
        marginBottom: spacing(2),
        backgroundColor: palette.background.primary,
        borderRadius: 5,
    },
    noDataMessageContainer: {
        marginTop: 13,
        marginBottom: 9,
    },
    paymentActivityYearSelect: {
        minWidth: 'auto',
        marginBottom: '5.5px',
    },
    paymentAmountTo: {
        marginTop: 26,
    },
    paymentAppliedToHeaderCell: {
        minWidth: 160,
    },
    paymentDateAmountGridColumn: {
        maxWidth: 'calc(50% - 16.75px)',
        flexBasis: 'calc(50% - 16.75px)',
    },
    paymentDateHeaderCell: {
        minWidth: 115,
    },
    paymentDateTo: {
        marginTop: 26,
    },
    paymentDetailTotal: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        '&.border-right-desktop': {
            borderRight: `1px solid ${palette.border.primary}`,
        },
        '&.padding-bottom-mobile': {
            paddingBottom: spacing(1),
        },
        '&.padding-top-mobile': {
            paddingTop: spacing(1),
        },
        '&:last': {
            borderRight: 'none',
        },
        '&_border_mobile': {
            borderBottom: `1px solid ${palette.border.primary}`,
        },
    },
    paymentDetailTotalHeader: {
        color: palette.text.secondary,
    },
    paymentDetailTotalInput: {
        marginBottom: 5,
    },
    paymentMethodCell: {
        minWidth: 95,
    },
    paymentTotalWrapper: {
        border: `1px solid ${palette.border.primary}`,
        padding: `${spacing(2)}px 0`,
    },
    searchButton: {
        paddingTop: typography.pxToRem(13),
        paddingBottom: typography.pxToRem(13),
        paddingLeft: typography.pxToRem(22),
        paddingRight: typography.pxToRem(22),
        [breakpoints.up('md')]: {
            fontWeight: typography.fontWeightBold,
            marginTop: 27,
        },
        '&.primary-search': {
            backgroundColor: '#4981F0',
        },
        '& .button-inner-container': {
            fontSize: typography.pxToRem(14),
            lineHeight: 1.125,
        },
    },
    searchInput: {
    },
    searchInputGridColumn: {
    },
    searchTabGridColumn: {
        '& .MuiButton-root': {
            paddingTop: 14.5,
            paddingBottom: 14.5,
        },
    },
    showMoreActivityLink: {
        display: 'flex',
        justifyContent: 'center',
    },
    spanLine: {
        fontSize: 14,
        fontWeight: 900,
        margin: '36px 11px 0 11px',
    },
    title: {
        fontSize: typography.pxToRem(20),
        lineHeight: 1,
    },
    xlsSpreadSheetBtn: {
        letterSpacing: 'normal',
        paddingTop: typography.pxToRem(13),
        paddingBottom: typography.pxToRem(13),
        paddingLeft: typography.pxToRem(22),
        paddingRight: typography.pxToRem(22),
        [breakpoints.down('sm')]: {
            width: '100%',
        },
        '& .button-inner-container': {
            fontSize: typography.pxToRem(14),
            fontWeight: typography.fontWeightMedium,
            lineHeight: 1.125,
        },
    },
}));

const BEM_SELECTOR_PREFIX = `${BEM_BLOCK_NAME}--payment_activity`;

const ALL_YEARS = -1;

const SEARCH_TYPE_BUTTON_OPTIONS = [
    {
        label: 'Basic Search',
        value: SearchType.Basic,
    },
    {
        label: 'Advanced Search',
        value: SearchType.Advanced,
    },
];

function PaymentActivity({
    accessToken,
    enabled = false,
    includeSpouseGiving,
    numberOfYearsToInclude = 1,
    showExcelDownloadButton = false,
    showSearchAndFilterControls = false,
    showStatistics = false,
    useRockAPIs = false,
}: PropTypes) {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile: boolean = useMediaQuery(theme.breakpoints.only('sm'));
    const isTablet: boolean = useMediaQuery(theme.breakpoints.only('md'));

    const timeZone = useMemo(() => (moment.tz.guess()), []);

    const yearOptions: SelectOption[] = useMemo<SelectOption[]>(() => {
        const regularYearOptions = Array.from({ length: numberOfYearsToInclude }, (_, i) => ({
            label: (CURRENT_YEAR - i).toString(),
            value: CURRENT_YEAR - i,
        }));

        return [
            ...regularYearOptions,
            {
                label: 'All Years',
                value: ALL_YEARS,
            },
        ];
    }, [numberOfYearsToInclude]);

    const [amountFrom, setAmountFrom] = useState<string | null>(null);
    const [parsedAmountFrom, setParsedAmountFrom] = useState<number>(NaN);
    const [amountTo, setAmountTo] = useState<string | null>(null);
    const [parsedAmountTo, setParsedAmountTo] = useState<number>(NaN);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [paymentDateFrom, setPaymentDateFrom] = useState<Moment | null>(null);
    const [paymentDateTo, setPaymentDateTo] = useState<Moment | null>(null);
    const [paymentActivityYear, setPaymentActivityYear] = useState<number>(ALL_YEARS);
    const [designationId, setDesignationId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [hasError, setHasError] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false);

    const [selectedSearchTab, setSelectedSearchTab] = useState<SearchType>(
        SearchType.Basic,
    );

    const [paymentHistoryData, setPaymentHistoryData] = useState<GivingPaymentHistory>(null);

    const canTriggerSearch = useRef(false);

    const initialStartDateQuery: string = useMemo(
        () => `startDate=${encodeURIComponent(moment.tz([CURRENT_YEAR - (numberOfYearsToInclude - 1)], timeZone).startOf('year').format())}&includeSpouseGiving=${includeSpouseGiving}`,
        [
            includeSpouseGiving,
            numberOfYearsToInclude,
            timeZone,
        ],
    );

    const isAdvancedSearchTabSelected: boolean = selectedSearchTab ===
        SearchType.Advanced;

    const getPaymentActivityList = useCallback((searchQuery: string) => {
        setIsFetching(true);
        setHasError(false);

        getGivingPaymentHistory(accessToken, searchQuery).then((res) => {
            setPaymentHistoryData(res);
        }).catch((response) => {
            setHasError(true);

            // eslint-disable-next-line no-console
            console.log('Error fetching Giving History:', response);
        }).finally(() => {
            setIsFetching(false);
        });
    }, [
        accessToken,
    ]);

    // Load Designations
    useEffect(() => {
        if (enabled) {
            getDesignationsFromCore()
                .then((theDesignations) => {
                    const designationSelectOptions = isArray(theDesignations?.results) &&
                        !isEmpty(theDesignations.results) ?
                        theDesignations.results.map((d) => ({
                            label: d.title,
                            value: d.id,
                        })) : [];

                    setDesignationOptions(designationSelectOptions);
                })
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.log('error fetching the Designations list:', error);
                });
        }
    }, [enabled]);

    // Get Giving Payment History Results
    useEffect(() => {
        if (enabled && !isNil(accessToken)) {
            const searchFormattedQuery = `${initialStartDateQuery}&timeZone=${timeZone}`;
            getPaymentActivityList(searchFormattedQuery);
            canTriggerSearch.current = false;
        }
    }, [
        accessToken,
        enabled,
        getPaymentActivityList,
        initialStartDateQuery,
        timeZone,
    ]);

    // Change placeholder text for the Date Picker Inputs
    useEffect(() => {
        const dateFromInput = document.getElementById(`${BEM_SELECTOR_PREFIX}_date_from_input`);

        if (!isNil(dateFromInput)) {
            dateFromInput.getElementsByTagName('input')[0].placeholder = 'From';
        }

        const dateToInput = document.getElementById(`${BEM_SELECTOR_PREFIX}_date_to_input`);

        if (!isNil(dateToInput)) {
            dateToInput.getElementsByTagName('input')[0].placeholder = 'To';
        }
    }, [selectedSearchTab]);

    const getSearchQueryParams = useCallback(() => {
        const queryStringParams = new URLSearchParams(initialStartDateQuery);

        if (!isAdvancedSearchTabSelected) {
            if (!isEmpty(searchTerm)) {
                queryStringParams.set('query', searchTerm);
            }

            if (!isNil(paymentActivityYear) && paymentActivityYear !== ALL_YEARS) {
                const paymentActivityStartDate = moment.tz([paymentActivityYear], timeZone).startOf('year').format();
                const paymentActivityEndDate = moment.tz([paymentActivityYear], timeZone).endOf('year').format();
                queryStringParams.set('startDate', paymentActivityStartDate);
                queryStringParams.set('endDate', paymentActivityEndDate);
            }
        } else { // Advanced Search
            if (!isNil(amountFrom)) {
                queryStringParams.set('amountRangeLowerLimit', amountFrom);
            }

            if (!isNil(amountTo)) {
                queryStringParams.set('amountRangeUpperLimit', amountTo);
            }

            if (!isEmpty(paymentDateFrom)) {
                queryStringParams.set('startDate', moment.tz(paymentDateFrom, timeZone).startOf('day').format());
            }

            if (!isEmpty(paymentDateTo)) {
                queryStringParams.set('endDate', moment.tz(paymentDateTo, timeZone).endOf('day').format());
            }

            if (!isNil(designationId)) {
                queryStringParams.set('designationIds', designationId.toString());
            }
        }

        if (!isEmpty(timeZone)) {
            queryStringParams.set('timeZone', timeZone);
        }

        queryStringParams.set('includeSpouseGiving', includeSpouseGiving.toString());

        return queryStringParams.toString();
    }, [
        amountFrom,
        amountTo,
        designationId,
        includeSpouseGiving,
        initialStartDateQuery,
        isAdvancedSearchTabSelected,
        paymentActivityYear,
        paymentDateFrom,
        paymentDateTo,
        searchTerm,
        timeZone,
    ]);

    const onClearAdvancedSearch = useCallback(() => {
        setAmountFrom(null);
        setAmountTo(null);
        setPaymentDateFrom(null);
        setPaymentDateTo(null);
        setDesignationId(null);
        canTriggerSearch.current = true;
    }, []);

    const onSearchClick = useCallback(() => {
        const searchFormattedQuery = getSearchQueryParams();
        getPaymentActivityList(searchFormattedQuery);
    }, [
        getPaymentActivityList,
        getSearchQueryParams,
    ]);

    const onChangePaymentActivityYear = useCallback((selectedOption) => {
        setPaymentActivityYear(selectedOption.value);
        canTriggerSearch.current = true;
    }, []);

    const onChangeDesignation = useCallback((selectedOption) => {
        setDesignationId(!isNil(selectedOption?.value) ? parseInt(selectedOption.value, 10) : null);
    }, []);

    const onChangeSearchTab = useCallback((selectedtab) => {
        setSelectedSearchTab(selectedtab);

        if (selectedtab === SearchType.Advanced) {
            setSearchTerm('');
            setPaymentActivityYear(ALL_YEARS);
            canTriggerSearch.current = true;
        } else {
            onClearAdvancedSearch();
        }

        onSearchClick();
    }, [
        onClearAdvancedSearch,
        onSearchClick,
    ]);

    const onClickXlsSpreadsheet = useCallback(async () => {
        if (useRockAPIs) {
            console.error('[Giving History Widget] Excel export is not currently supported when using Rock RMS compatible APIs');
            return;
        }

        let startDate = '';
        let endDate = null;

        if (isAdvancedSearchTabSelected && !isEmpty(paymentDateFrom)) {
            startDate = moment.tz(paymentDateFrom, timeZone).format();
        } else if (!isNil(paymentActivityYear) && paymentActivityYear !== ALL_YEARS) {
            startDate = moment.tz([paymentActivityYear], timeZone).startOf('year').format();
            endDate = moment.tz([paymentActivityYear], timeZone).endOf('year').format();
        } else {
            startDate = moment.tz([CURRENT_YEAR - (numberOfYearsToInclude - 1)], timeZone).startOf('year').format();
        }

        if (!isEmpty(paymentDateTo)) {
            endDate = moment.tz(paymentDateTo, timeZone).endOf('day').format();
        }

        try {
            const response = await requestGivingExcelDataExport(accessToken, {
                amountRangeLowerLimit: amountFrom ?? null,
                amountRangeUpperLimit: amountTo ?? null,
                designationIds: designationId?.toString() ?? null,
                endDate,
                format: 'Excel',
                includeSpouseGiving,
                query: searchTerm,
                startDate,
                timeZone,
            });

            if (response.id) {
                const intervalId = setInterval(async () => {
                    try {
                        const statusResponse = await requestExportStatus(accessToken, response.id);

                        if (statusResponse.status === 'Completed') {
                            window.location.href = statusResponse.downloadUrl;
                            clearInterval(intervalId);
                        }
                    } catch (statusError) {
                        console.error('Error checking export status', statusError);
                    }
                }, 1000);
            }
        } catch (error) {
            console.error('Error requesting Excel spreadsheet', error);
        }
    }, [
        accessToken,
        amountFrom,
        amountTo,
        designationId,
        includeSpouseGiving,
        isAdvancedSearchTabSelected,
        numberOfYearsToInclude,
        paymentActivityYear,
        paymentDateFrom,
        paymentDateTo,
        searchTerm,
        timeZone,
        useRockAPIs,
    ]);

    const onChangePaymentAmountFrom = useCallback((value: string) => {
        setAmountFrom(value);
        setParsedAmountFrom(!isEmpty(value) ? parseFloat(value) : NaN);
    }, []);

    const onChangePaymentAmountTo = useCallback((value: string) => {
        setAmountTo(value);
        setParsedAmountTo(!isEmpty(value) ? parseFloat(value) : NaN);
    }, []);

    const onChangePaymentDateFrom = useCallback(({ date }) => {
        if (moment.isMoment(date)) {
            setPaymentDateFrom(date);
        }
    }, []);

    const onChangePaymentDateTo = useCallback(({ date }) => {
        if (moment.isMoment(date)) {
            setPaymentDateTo(date);
        }
    }, []);

    // Query new search results on payment activity year changed
    useEffect(() => {
        if (!isNil(paymentActivityYear) && canTriggerSearch.current) {
            onSearchClick();
            canTriggerSearch.current = false;
        }
    }, [
        onSearchClick,
        paymentActivityYear,
    ]);

    // Query new search results when clearSearchInput triggered
    useEffect(() => {
        if ((isEmpty(searchTerm) || isEmpty(paymentDateFrom)) && canTriggerSearch.current) {
            onSearchClick();
            canTriggerSearch.current = false;
        }
    }, [
        onSearchClick,
        searchTerm,
        paymentDateFrom,
    ]);

    const onNextPaymentActivityPage = useCallback(() => {
        const pageNumber = paymentHistoryData?.pagination?.pageNumber;
        const pageSize = paymentHistoryData?.pagination?.pageSize;

        let searchFormattedQuery = getSearchQueryParams();
        searchFormattedQuery += `&pageNumber=${pageNumber + 1}&pageSize=${pageSize}`;

        setIsFetchingNextPage(true);

        getGivingPaymentHistory(
            accessToken,
            searchFormattedQuery,
        ).then((res) => {
            const newResults = {
                ...res,
                results: [
                    ...paymentHistoryData.results,
                    ...res.results,
                ],
            };

            setIsFetchingNextPage(false);
            setPaymentHistoryData(newResults);
        });
    }, [
        accessToken,
        getSearchQueryParams,
        paymentHistoryData,
    ]);

    const onSearchInputChange = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    const onSearchInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
        if (isEnterKeyCode(event) && !isNil(searchTerm)) {
            onSearchClick();
        }
    }, [
        onSearchClick,
        searchTerm,
    ]);

    const onSearchInputClearClick = useCallback(() => {
        setSearchTerm('');
        canTriggerSearch.current = true;
    }, []);

    const hasAmountFrom: boolean = !isEmpty(amountFrom);
    const hasAmountTo: boolean = !isEmpty(amountTo);
    const hasPaymentDateFrom: boolean = !isEmpty(paymentDateFrom);
    const hasPaymentDateTo: boolean = !isEmpty(paymentDateTo);

    const isAllDataEmpty: boolean = !hasAmountFrom &&
        !hasAmountTo &&
        !hasPaymentDateFrom &&
        !hasPaymentDateTo &&
        isNil(designationId); // it's either `null` or a number, so deliberately using `isNil()` instead of `isEmpty()`

    const isAmountRangeInvalid: boolean = hasAmountFrom &&
        hasAmountTo &&
        (isNaN(parsedAmountFrom) || isNaN(parsedAmountTo) || parsedAmountTo < parsedAmountFrom);

    const isDateRangeInvalid: boolean = hasPaymentDateFrom &&
        hasPaymentDateTo &&
        paymentDateTo.isBefore(paymentDateFrom);

    const isSearchButtonDisabled: boolean = isAllDataEmpty ||
        isAmountRangeInvalid ||
        isDateRangeInvalid;

    const hasResults: boolean = !isEmpty(paymentHistoryData?.results);

    if (!enabled) {
        return null;
    }

    if (isNil(accessToken)) {
        return null;
    }

    return (
        <div className={ClassNames(classes.root, BEM_SELECTOR_PREFIX)}>
            <Grid
                className={`${BEM_SELECTOR_PREFIX}_grid`}
                spacing={2}
            >
                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_title_container`}
                    sm={12}
                >
                    <Typography
                        className={ClassNames(classes.title, `${BEM_SELECTOR_PREFIX}_title`)}
                        variant="h2"
                    >
                        {t('paymentActivity.title')}
                    </Typography>
                </Grid.Column>

                {numberOfYearsToInclude > 1 && (
                    <Grid.Column
                        className={`${BEM_SELECTOR_PREFIX}_copy_container`}
                        sm={12}
                    >
                        <Typography
                            className={`${BEM_SELECTOR_PREFIX}_copy`}
                            variant="body1"
                        >
                            {numberOfYearsToInclude === 2 ?
                                t('paymentActivity.blurbTwoYears') :
                                t('paymentActivity.blurbMultipleYears', { numPriorYears: numberOfYearsToInclude - 1 })}
                        </Typography>
                    </Grid.Column>
                )}

                {showSearchAndFilterControls && (
                    <React.Fragment>
                        <Grid.Column
                            className={ClassNames(classes.searchTabGridColumn, `${BEM_SELECTOR_PREFIX}_search_tabs_container`)}
                            md={(isTablet || isAdvancedSearchTabSelected) ? 12 : 4}
                            sm={12}
                        >
                            <ButtonGroup
                                buttons={SEARCH_TYPE_BUTTON_OPTIONS}
                                classes={{
                                    root: `${BEM_SELECTOR_PREFIX}_search_tabs`,
                                }}
                                fullWidth
                                onChange={onChangeSearchTab}
                                value={selectedSearchTab}
                            />
                        </Grid.Column>

                        {!isAdvancedSearchTabSelected ? (
                            <React.Fragment>
                                <Grid.Column
                                    className={ClassNames(classes.searchInputGridColumn, `${BEM_SELECTOR_PREFIX}_search_input_container`)}
                                    md={isTablet ? 8 : 6}
                                    sm={12}
                                >
                                    <Input
                                        className={ClassNames(classes.searchInput, `${BEM_SELECTOR_PREFIX}_search_input`)}
                                        fluid
                                        icon={
                                            searchTerm ? (
                                                <Icon
                                                    color="static"
                                                    compact
                                                    id={`${BEM_SELECTOR_PREFIX}_search_input_clear_icon_button`}
                                                    onClick={onSearchInputClearClick}
                                                    title="Clear"
                                                    type="false"
                                                />
                                            ) : (
                                                <Icon
                                                    compact
                                                    title="Search"
                                                    type="search"
                                                />
                                            )
                                        }
                                        id={`${BEM_SELECTOR_PREFIX}_search_input`}
                                        onChange={onSearchInputChange}
                                        onKeyDown={onSearchInputKeyDown}
                                        placeholder="Search"
                                        value={searchTerm}
                                    />
                                </Grid.Column>

                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_year_select_container`}
                                    md={isTablet ? 4 : 2}
                                    sm={12}
                                >
                                    <Select
                                        className={ClassNames(classes.paymentActivityYearSelect, `${BEM_SELECTOR_PREFIX}_year_select`)}
                                        fluid
                                        id={`${BEM_SELECTOR_PREFIX}_year_select`}
                                        onChange={onChangePaymentActivityYear}
                                        options={yearOptions}
                                        placeholder="Select Year"
                                        value={paymentActivityYear?.toString()}
                                    />
                                </Grid.Column>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_advanced_search_date_inputs_container`}
                                    md={6}
                                    sm={12}
                                >
                                    <Grid>
                                        <Grid.Column sm={12}>
                                            <Grid spacing={0}>
                                                <Grid.Column
                                                    className={ClassNames(classes.paymentDateAmountGridColumn, `${BEM_SELECTOR_PREFIX}_date_from_input_container`)}
                                                    sm={6}
                                                >
                                                    <DatePickerInput
                                                        className={ClassNames(classes.inputLabel, `${BEM_SELECTOR_PREFIX}_date_from_input`)}
                                                        dataTestId={`${BEM_SELECTOR_PREFIX}_date_from_input`}
                                                        date={paymentDateFrom}
                                                        fluid
                                                        id={`${BEM_SELECTOR_PREFIX}_date_from_input`}
                                                        label="Payment Date"
                                                        onChange={onChangePaymentDateFrom}
                                                        placeholder="From"
                                                        tabIndex={0}
                                                    />
                                                </Grid.Column>

                                                <Grid.Column sm={0}>
                                                    <div className={classes.spanLine}>
                                                        —
                                                    </div>
                                                </Grid.Column>

                                                <Grid.Column
                                                    className={ClassNames(classes.paymentDateAmountGridColumn, `${BEM_SELECTOR_PREFIX}_date_to_input_container`)}
                                                    sm={6}
                                                >
                                                    <DatePickerInput
                                                        className={ClassNames(classes.inputLabel, classes.paymentDateTo, `${BEM_SELECTOR_PREFIX}_date_to_input`)}
                                                        dataTestId={`${BEM_SELECTOR_PREFIX}_date_to_input`}
                                                        date={paymentDateTo}
                                                        fluid
                                                        id={`${BEM_SELECTOR_PREFIX}_date_to_input`}
                                                        maxDate={moment()}
                                                        onChange={onChangePaymentDateTo}
                                                        placeholder="To"
                                                        tabIndex={0}
                                                    />
                                                </Grid.Column>
                                            </Grid>
                                        </Grid.Column>
                                    </Grid>
                                </Grid.Column>

                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_advanced_search_amount_inputs_container`}
                                    md={6}
                                    sm={12}
                                >
                                    <Grid>
                                        <Grid.Column sm={12}>
                                            <Grid spacing={0}>
                                                <Grid.Column
                                                    className={ClassNames(classes.paymentDateAmountGridColumn, `${BEM_SELECTOR_PREFIX}_amount_from_input_container`)}
                                                    sm={6}
                                                >
                                                    <Input
                                                        className={ClassNames(classes.inputLabel, `${BEM_SELECTOR_PREFIX}_amount_from_input`)}
                                                        fluid
                                                        id={`${BEM_SELECTOR_PREFIX}_amount_from_input`}
                                                        label="Payment Amount"
                                                        onChange={onChangePaymentAmountFrom}
                                                        placeholder="From"
                                                        tabIndex={0}
                                                        value={amountFrom ?? ''}
                                                    />
                                                </Grid.Column>

                                                <Grid.Column sm={0}>
                                                    <div className={classes.spanLine}>
                                                        —
                                                    </div>
                                                </Grid.Column>

                                                <Grid.Column
                                                    className={ClassNames(classes.paymentDateAmountGridColumn, `${BEM_SELECTOR_PREFIX}_amount_to_input_container`)}
                                                    sm={6}
                                                >
                                                    <Input
                                                        className={ClassNames(classes.inputLabel, classes.paymentAmountTo, `${BEM_SELECTOR_PREFIX}_amount_to_input`)}
                                                        fluid
                                                        id={`${BEM_SELECTOR_PREFIX}_amount_to_input`}
                                                        onChange={onChangePaymentAmountTo}
                                                        placeholder="To"
                                                        tabIndex={0}
                                                        value={amountTo ?? ''}
                                                    />
                                                </Grid.Column>
                                            </Grid>
                                        </Grid.Column>
                                    </Grid>
                                </Grid.Column>

                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_designation_select_container`}
                                    md={6}
                                    sm={12}
                                >
                                    <Select
                                        className={ClassNames(classes.inputLabel, `${BEM_SELECTOR_PREFIX}_designation_select`)}
                                        fluid
                                        id={`${BEM_SELECTOR_PREFIX}_designation_select`}
                                        label="Designation"
                                        onChange={onChangeDesignation}
                                        options={designationOptions}
                                        placeholder="Select"
                                        searchable
                                        tabIndex={0}
                                        value={designationId}
                                    />
                                </Grid.Column>

                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_search_button_container`}
                                    md={2}
                                    sm={12}
                                >
                                    <Button
                                        className={ClassNames(
                                            classes.searchButton,
                                            `${BEM_SELECTOR_PREFIX}_search_button`,
                                            {
                                                'primary-search': !isSearchButtonDisabled,
                                                [`${BEM_SELECTOR_PREFIX}_search_button-disabled`]: isSearchButtonDisabled,
                                            },
                                        )}
                                        disabled={isSearchButtonDisabled}
                                        onClick={onSearchClick}
                                        size="lg"
                                    >
                                        Search
                                    </Button>
                                </Grid.Column>

                                <Grid.Column
                                    className={`${BEM_SELECTOR_PREFIX}_clear_search_link_button_container`}
                                    md={2}
                                    sm={12}
                                >
                                    <div className={classes.clearSearchLink}>
                                        <A
                                            className={`${BEM_SELECTOR_PREFIX}_clear_search_link_button`}
                                            id={`${BEM_SELECTOR_PREFIX}_clear_search_link_button`}
                                            onClick={onClearAdvancedSearch}
                                            tabIndex={0}
                                        >
                                            Clear Search
                                        </A>
                                    </div>
                                </Grid.Column>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                )}

                {isFetching && (
                    <Grid.Column
                        className={`${BEM_SELECTOR_PREFIX}_fetching_activity_indicator_container`}
                        sm={12}
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            justifyContent="center"
                            mb={9}
                            mt={9}
                        >
                            <ActivityIndicator />
                        </Box>
                    </Grid.Column>
                )}

                {hasError && (
                    <Error className={`${BEM_SELECTOR_PREFIX}_error_message`} />
                )}

                {!isFetching && !hasError && (hasResults ? (
                    <React.Fragment>
                        {showStatistics && (
                            <Grid.Column
                                className={ClassNames(`${BEM_SELECTOR_PREFIX}_container`, `${BEM_SELECTOR_PREFIX}_statistics_outer_container`)}
                                sm={12}
                            >
                                <Grid
                                    alignItems="center"
                                    className={ClassNames(classes.paymentTotalWrapper, `${BEM_SELECTOR_PREFIX}_statistics_inner_container`)}
                                    justifyContent="center"
                                >
                                    <Grid.Column
                                        className={`${BEM_SELECTOR_PREFIX}_statistic_item_outer_container`}
                                        md={3}
                                        sm={6}
                                    >
                                        <div className={ClassNames(
                                            classes.paymentDetailTotal,
                                            'border-right-desktop',
                                            `${BEM_SELECTOR_PREFIX}_statistic_item_inner_container`,
                                            {
                                                'padding-bottom-mobile': isMobile,
                                            },
                                        )}
                                        >
                                            <Typography
                                                className={classes.paymentDetailTotal}
                                                variant="h3"
                                            >
                                                {
                                                    // eslint-disable-next-line max-len
                                                    `${formatNumberAsCurrency(paymentHistoryData?.statistics?.currentMonthTotal?.amount, paymentHistoryData?.statistics?.currentMonthTotal?.currency)}`
                                                }
                                            </Typography>

                                            <Typography
                                                className={classes.paymentDetailTotalHeader}
                                                variant="body2"
                                            >
                                                {t('paymentActivity.statistics.thisMonth')}
                                            </Typography>
                                        </div>
                                    </Grid.Column>

                                    <Grid.Column
                                        className={`${BEM_SELECTOR_PREFIX}_statistic_item_outer_container`}
                                        md={3}
                                        sm={6}
                                    >
                                        <div className={ClassNames(
                                            classes.paymentDetailTotal,
                                            `${BEM_SELECTOR_PREFIX}_statistic_item_inner_container`,
                                            {
                                                'border-right-desktop': !isMobile,
                                                'padding-bottom-mobile': isMobile,
                                            },
                                        )}
                                        >
                                            <Typography
                                                className={classes.paymentDetailTotalInput}
                                                variant="h3"
                                            >
                                                {
                                                    // eslint-disable-next-line max-len
                                                    `${formatNumberAsCurrency(paymentHistoryData?.statistics?.currentYearTotal?.amount, paymentHistoryData?.statistics?.currentYearTotal?.currency)}`
                                                }
                                            </Typography>

                                            <Typography
                                                className={classes.paymentDetailTotalHeader}
                                                variant="body2"
                                            >
                                                {t('paymentActivity.statistics.yearToDate')}
                                            </Typography>
                                        </div>
                                    </Grid.Column>

                                    <Grid.Column
                                        className={`${BEM_SELECTOR_PREFIX}_statistic_item_outer_container`}
                                        md={3}
                                        sm={6}
                                    >
                                        <div className={ClassNames(
                                            classes.paymentDetailTotal,
                                            'border-right-desktop',
                                            `${BEM_SELECTOR_PREFIX}_statistic_item_inner_container`,
                                            {
                                                'padding-top-mobile': isMobile,
                                            },
                                        )}
                                        >
                                            <Typography
                                                className={classes.paymentDetailTotalInput}
                                                variant="h3"
                                            >
                                                {
                                                    // eslint-disable-next-line max-len
                                                    `${formatNumberAsCurrency(paymentHistoryData?.statistics?.previousYearToDateTotal?.amount, paymentHistoryData?.statistics?.previousYearToDateTotal?.currency)}`
                                                }
                                            </Typography>

                                            <Typography
                                                className={classes.paymentDetailTotalHeader}
                                                variant="body2"
                                            >
                                                {t('paymentActivity.statistics.previousYearToDate')}
                                            </Typography>
                                        </div>
                                    </Grid.Column>

                                    <Grid.Column
                                        className={`${BEM_SELECTOR_PREFIX}_statistic_item_outer_container`}
                                        md={3}
                                        sm={6}
                                    >
                                        <div className={ClassNames(
                                            classes.paymentDetailTotal,
                                            `${BEM_SELECTOR_PREFIX}_statistic_item_inner_container`,
                                            {
                                                'padding-top-mobile': isMobile,
                                            },
                                        )}
                                        >
                                            <Typography
                                                className={classes.paymentDetailTotalInput}
                                                variant="h3"
                                            >
                                                {
                                                    // eslint-disable-next-line max-len
                                                    `${formatNumberAsCurrency(paymentHistoryData?.statistics?.previousYearTotal?.amount, paymentHistoryData?.statistics?.previousYearTotal?.currency)}`
                                                }
                                            </Typography>

                                            <Typography
                                                className={classes.paymentDetailTotalHeader}
                                                variant="body2"
                                            >
                                                {t('paymentActivity.statistics.previousYearTotal')}
                                            </Typography>
                                        </div>
                                    </Grid.Column>
                                </Grid>
                            </Grid.Column>
                        )}

                        {showExcelDownloadButton && (
                            <Grid.Column
                                className={ClassNames(`${BEM_SELECTOR_PREFIX}_container`, `${BEM_SELECTOR_PREFIX}_excel_button_container`)}
                                sm={12}
                            >
                                <Button
                                    className={ClassNames(classes.xlsSpreadSheetBtn, `${BEM_SELECTOR_PREFIX}_excel_button`)}
                                    color="success"
                                    fullWidth={false}
                                    id={`${BEM_SELECTOR_PREFIX}_excel_button`}
                                    onClick={onClickXlsSpreadsheet}
                                    size="lg"
                                >
                                    Excel Spreadsheet
                                </Button>
                            </Grid.Column>
                        )}

                        <Grid.Column
                            className={ClassNames(`${BEM_SELECTOR_PREFIX}_container`, `${BEM_SELECTOR_PREFIX}_table_container`)}
                            sm={12}
                        >
                            <Table
                                basic
                                className={ClassNames(classes.paymentDetailsTable, `${BEM_SELECTOR_PREFIX}_table`)}
                                stickyColumnCount={isMobile ? 1 : 0}
                                theme={null} // TODO/FIXME: This oddity is currently necessary to prevent a TypeScript error: Property 'theme' is missing in type '{ children: Element[]; basic: true; className: any; stickyColumnCount: number; }' but required in type 'TablePropTypes'
                            >
                                <Table.Header>
                                    <Table.Row fontSize="xsmall">
                                        <Table.HeaderCell
                                            className={ClassNames(classes.paymentDateHeaderCell, `${BEM_SELECTOR_PREFIX}_table_header_date`)}
                                        >
                                            {t('paymentActivity.table.paymentDate')}
                                        </Table.HeaderCell>

                                        <Table.HeaderCell
                                            className={`${BEM_SELECTOR_PREFIX}_table_header_by`}
                                        >
                                            {t('paymentActivity.table.by')}
                                        </Table.HeaderCell>

                                        <Table.HeaderCell
                                            className={
                                                ClassNames(classes.paymentAppliedToHeaderCell, `${BEM_SELECTOR_PREFIX}_table_header_applied_to`)
                                            }
                                        >
                                            {t('paymentActivity.table.appliedTo')}
                                        </Table.HeaderCell>

                                        <Table.HeaderCell
                                            className={ClassNames(classes.paymentMethodCell, `${BEM_SELECTOR_PREFIX}_table_header_method`)}
                                        >
                                            {t('paymentActivity.table.method')}
                                        </Table.HeaderCell>

                                        <Table.HeaderCell
                                            className={ClassNames(classes.paymentMethodCell, `${BEM_SELECTOR_PREFIX}_table_header_amount`)}
                                            textAlign="right"
                                        >
                                            {t('paymentActivity.table.amount')}
                                        </Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>

                                <Table.Body>
                                    {paymentHistoryData.results.map((data) => {
                                        const paymentDate = moment(data.paymentDateUtc).format('L');

                                        return (
                                            <Table.Row key={data.id}>
                                                <Table.Cell
                                                    className={`${BEM_SELECTOR_PREFIX}_table_data_cell_date`}
                                                >
                                                    <Typography
                                                        className="bold"
                                                        variant="h5"
                                                    >
                                                        {paymentDate}
                                                    </Typography>
                                                </Table.Cell>

                                                <Table.Cell
                                                    className={`${BEM_SELECTOR_PREFIX}_table_data_cell_by`}
                                                >
                                                    <Typography variant="h5">
                                                        {data.giverName}
                                                    </Typography>
                                                </Table.Cell>

                                                <Table.Cell
                                                    className={`${BEM_SELECTOR_PREFIX}_table_data_cell_applied_to`}
                                                >
                                                    <Typography variant="h5">
                                                        {data.appliedTo}
                                                    </Typography>
                                                </Table.Cell>

                                                <Table.Cell
                                                    className={`${BEM_SELECTOR_PREFIX}_table_data_cell_method`}
                                                >
                                                    <Typography variant="h5">
                                                        {data.paymentMethod}
                                                    </Typography>
                                                </Table.Cell>

                                                <Table.Cell
                                                    className={`${BEM_SELECTOR_PREFIX}_table_data_cell_amount`}
                                                    textAlign="right"
                                                >
                                                    <Typography
                                                        className="bold"
                                                        variant="h5"
                                                    >
                                                        {
                                                            // eslint-disable-next-line max-len
                                                            `${CurrencySymbol[data?.amount?.currency]}${data?.amount?.amount.toFixed(2).replace(/[.,]00$/, '')}`
                                                        }
                                                    </Typography>
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                        </Grid.Column>

                        {isFetchingNextPage && (
                            <Grid.Column
                                className={`${BEM_SELECTOR_PREFIX}_next_page_loading_indicator_container`}
                                sm={12}
                            >
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <ActivityIndicator
                                        size={44}
                                    />
                                </Box>
                            </Grid.Column>
                        )}

                        {paymentHistoryData?.containsMoreRecords && (
                            <Grid.Column
                                className={ClassNames(classes.gridColumn, `${BEM_SELECTOR_PREFIX}_show_more_link_button_container`)}
                                sm={12}
                            >
                                <A
                                    className={ClassNames(classes.showMoreActivityLink, `${BEM_SELECTOR_PREFIX}_show_more_link_button`)}
                                    disable={isFetchingNextPage}
                                    id={`${BEM_SELECTOR_PREFIX}_show_more_link_button`}
                                    onClick={onNextPaymentActivityPage}
                                >
                                    {t('paymentActivity.showMoreActivity')}
                                </A>
                            </Grid.Column>
                        )}
                    </React.Fragment>
                ) : (
                    <div className={ClassNames(classes.noDataMessageContainer, `${BEM_SELECTOR_PREFIX}_no_data_message_container`)}>
                        <NoResultFound className={`${BEM_SELECTOR_PREFIX}_no_data_message`} />
                    </div>
                ))}
            </Grid>
        </div>
    );
}

export default PaymentActivity;
